import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBusinessUnits, usePeriods } from './hooks';
import type { BusinessUnit, Period } from '@/types/schemas';

export const COMPANY_SCOPE = 'company';

interface SelectionValue {
  units: BusinessUnit[];
  periods: Period[];
  loading: boolean;
  error: string | null;
  scope: string;
  setScope: (scope: string) => void;
  selectedUnit: BusinessUnit | null;
  selectedPeriod: Period | null;
  periodId: string | null;
  setPeriodId: (id: string) => void;
  reload: () => void;
}

const SelectionContext = createContext<SelectionValue | null>(null);

export function SelectionProvider({ children }: { children: ReactNode }) {
  const unitsQuery = useBusinessUnits();
  const periodsQuery = usePeriods();
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep links: /pnl?unit=<id>&period=<year-month> restores the same report.
  const [scope, setScopeState] = useState<string>(searchParams.get('unit') ?? COMPANY_SCOPE);
  const [periodId, setPeriodIdState] = useState<string | null>(null);
  const urlPeriod = searchParams.get('period');

  const syncParams = (nextScope: string, nextPeriodKey: string | null) => {
    setSearchParams(
      (params) => {
        if (nextScope === COMPANY_SCOPE) params.delete('unit');
        else params.set('unit', nextScope);
        if (nextPeriodKey) params.set('period', nextPeriodKey);
        return params;
      },
      { replace: true },
    );
  };

  const units = useMemo(() => unitsQuery.data ?? [], [unitsQuery.data]);
  const periods = useMemo(() => periodsQuery.data ?? [], [periodsQuery.data]);

  // Default to the first restaurant once units arrive (unless a deep link set one).
  useEffect(() => {
    if (scope !== COMPANY_SCOPE || units.length === 0) return;
    if (searchParams.get('unit')) return;
    const restaurant = units.find((u) => u.type === 'RESTAURANT');
    if (restaurant) setScopeState(restaurant.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, scope]);

  // Default to the newest period, or the one named in the URL.
  useEffect(() => {
    if (periodId || periods.length === 0) return;
    const fromUrl = urlPeriod
      ? periods.find((p) => `${p.year}-${String(p.month).padStart(2, '0')}` === urlPeriod)
      : null;
    setPeriodIdState((fromUrl ?? periods[0]).id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods, periodId]);

  const setScope = (next: string) => {
    setScopeState(next);
    const period = periods.find((p) => p.id === periodId);
    syncParams(next, period ? `${period.year}-${String(period.month).padStart(2, '0')}` : null);
  };

  const setPeriodId = (next: string) => {
    setPeriodIdState(next);
    const period = periods.find((p) => p.id === next);
    syncParams(scope, period ? `${period.year}-${String(period.month).padStart(2, '0')}` : null);
  };

  const value = useMemo<SelectionValue>(() => {
    const error =
      unitsQuery.error instanceof Error
        ? unitsQuery.error.message
        : periodsQuery.error instanceof Error
          ? periodsQuery.error.message
          : null;
    return {
      units,
      periods,
      loading: unitsQuery.isPending || periodsQuery.isPending,
      error,
      scope,
      setScope,
      selectedUnit: units.find((u) => u.id === scope) ?? null,
      selectedPeriod: periods.find((p) => p.id === periodId) ?? periods[0] ?? null,
      periodId,
      setPeriodId,
      reload: () => {
        void unitsQuery.refetch();
        void periodsQuery.refetch();
      },
    };
  }, [units, periods, scope, periodId, unitsQuery, periodsQuery]);

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
}

export function useSelection(): SelectionValue {
  const context = useContext(SelectionContext);
  if (!context) throw new Error('useSelection must be used inside SelectionProvider');
  return context;
}
